import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { clsx } from '../../../ds/clsx';
import { normalizeTimeInput, parseTimeInput } from '../../../lib/time-input';
import { Popover } from '../../../ds/components';
import {
  WEEKDAYS,
  addDays,
  formatDayKey,
  monthGrid,
  monthTitle,
  parseGermanDate,
  partsOf,
  todayKey,
} from '../format';
import styles from './editor.module.css';

/** Label row (label + right hint) above a control. */
export function EditorField({
  label,
  hint,
  error,
  tight = false,
  children,
}: {
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  tight?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <div className={styles.field}>
      <div className={styles.fieldHead}>
        <span className={styles.fieldLabel}>{label}</span>
        {hint ? <span className={styles.fieldHint}>{hint}</span> : null}
      </div>
      <div className={clsx(styles.fieldBody, tight && styles.fieldBodyTight)}>{children}</div>
      {error ? (
        <div className={styles.fieldError} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export interface ChipOption<T extends string> {
  value: T;
  label: string;
  disabled?: boolean | undefined;
}

/** Single-choice chips (chipLight): a radiogroup of pills. */
export function ChipGroup<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<ChipOption<T>>;
  value: T | null;
  onChange: (value: T) => void;
}) {
  return (
    <div className={styles.chips} role="radiogroup" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          disabled={option.disabled}
          className={clsx(styles.chip, option.value === value && styles.chipActive)}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Multi-choice chips (the "Bestätigung senden per" style): toggles with aria-pressed. */
export function ChipToggles<T extends string>({
  label,
  options,
  values,
  onToggle,
}: {
  label: string;
  options: ReadonlyArray<ChipOption<T>>;
  values: ReadonlySet<T>;
  onToggle: (value: T) => void;
}) {
  return (
    <div className={styles.chips} role="group" aria-label={label}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={values.has(option.value)}
          disabled={option.disabled}
          className={clsx(styles.chip, values.has(option.value) && styles.chipActive)}
          onClick={() => onToggle(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Checklist rows (E6, E7): sunken rows with a 15 px box; static rows show the mark without a control. */
export function CheckList({
  items,
  static: isStatic = false,
}: {
  items: ReadonlyArray<{
    id: string;
    label: string;
    meta?: string | null | undefined;
    checked: boolean;
    onChange?: ((checked: boolean) => void) | undefined;
  }>;
  static?: boolean | undefined;
}) {
  return (
    <div className={styles.list}>
      {items.map((item) =>
        isStatic || !item.onChange ? (
          <div key={item.id} className={clsx(styles.listItem, styles.listItemStatic)}>
            <span className={clsx(styles.listBox, item.checked && styles.listBoxOn)} aria-hidden="true">
              {item.checked ? '✓' : ''}
            </span>
            <span className={styles.listText}>
              <span className={styles.listLabel}>{item.label}</span>
              {item.meta ? <span className={styles.listMeta}>{item.meta}</span> : null}
            </span>
          </div>
        ) : (
          <label key={item.id} className={styles.listItem}>
            <input
              type="checkbox"
              className={styles.listInput}
              checked={item.checked}
              onChange={(event) => item.onChange?.(event.target.checked)}
            />
            <span className={clsx(styles.listBox, item.checked && styles.listBoxOn)} aria-hidden="true">
              {item.checked ? '✓' : ''}
            </span>
            <span className={styles.listText}>
              <span className={styles.listLabel}>{item.label}</span>
              {item.meta ? <span className={styles.listMeta}>{item.meta}</span> : null}
            </span>
          </label>
        ),
      )}
    </div>
  );
}

/** The month sheet of the calendar popover (T4/T1): ‹ › header, Mo–So, the day grid, the footer note. */
export function CalendarSheet({
  value,
  onSelect,
  note = 'Uhrzeit steht im Feld daneben. Ohne Uhrzeit wird 09:00 gesetzt.',
  wide = false,
  header,
  footer,
}: {
  value: string | null;
  onSelect: (key: string) => void;
  note?: string | null | undefined;
  wide?: boolean | undefined;
  header?: ReactNode | undefined;
  footer?: ReactNode | undefined;
}) {
  const start = partsOf(value ?? todayKey());
  const [view, setView] = useState({ year: start.year, month: start.month });
  useEffect(() => {
    if (value) {
      const parts = partsOf(value);
      setView({ year: parts.year, month: parts.month });
    }
  }, [value]);
  const cells = monthGrid(view.year, view.month);
  function shift(delta: number) {
    setView((current) => {
      const month = current.month + delta;
      return { year: current.year + Math.floor(month / 12), month: ((month % 12) + 12) % 12 };
    });
  }
  return (
    <div className={clsx(styles.calendar, wide && styles.calendarWide)}>
      {header}
      <div className={styles.calendarHead}>
        <span className={styles.calendarTitle}>{monthTitle(view.year, view.month)}</span>
        <span className={styles.calendarNav}>
          <button
            type="button"
            className={styles.calendarNavButton}
            aria-label="Vormonat"
            onClick={() => shift(-1)}
          >
            ‹
          </button>
          <button
            type="button"
            className={styles.calendarNavButton}
            aria-label="Folgemonat"
            onClick={() => shift(1)}
          >
            ›
          </button>
        </span>
      </div>
      <div className={styles.calendarGrid} role="grid" aria-label={monthTitle(view.year, view.month)}>
        {WEEKDAYS.map((day) => (
          <span key={day} className={styles.calendarWeekday} role="columnheader">
            {day}
          </span>
        ))}
        {cells.map((cell) => (
          <button
            key={cell.key}
            type="button"
            role="gridcell"
            tabIndex={cell.inMonth ? 0 : -1}
            aria-label={formatDayKey(cell.key)}
            aria-selected={cell.key === value}
            className={clsx(
              styles.calendarDay,
              !cell.inMonth && styles.calendarDayOutside,
              cell.key === value && styles.calendarDaySelected,
            )}
            onClick={() => onSelect(cell.key)}
          >
            {cell.inMonth ? cell.day : ''}
          </button>
        ))}
      </div>
      {note ? <p className={styles.calendarNote}>{note}</p> : null}
      {footer}
    </div>
  );
}

/**
 * Date box ("19.09.2026") with the calendar popover on click/focus, and the time box next to it.
 * `date` is a day key or '' ; `time` is HH:MM or ''.
 */
export function DateTimeBoxes({
  label,
  date,
  time,
  onDateChange,
  onTimeChange,
  small = false,
  invalid = false,
  defaultOpen = false,
  timePlaceholder = 'HH:MM',
  withTime = true,
}: {
  label: string;
  date: string;
  time: string;
  onDateChange: (key: string) => void;
  onTimeChange: (time: string) => void;
  small?: boolean | undefined;
  invalid?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  timePlaceholder?: string | undefined;
  /** false hides the time box (the self-disclosure follow-up is a day). */
  withTime?: boolean | undefined;
}) {
  const id = useId();
  const anchorRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(defaultOpen);
  const [text, setText] = useState(date ? formatDayKey(date) : '');
  useEffect(() => {
    setText(date ? formatDayKey(date) : '');
  }, [date]);

  function commitText() {
    if (text.trim() === '') {
      onDateChange('');
      return;
    }
    const parsed = parseGermanDate(text);
    if (parsed) onDateChange(parsed);
    else setText(date ? formatDayKey(date) : '');
  }

  return (
    <span className={styles.followUpFields}>
      <input
        ref={anchorRef}
        id={`${id}-date`}
        className={clsx(styles.dateBox, small && styles.dateBoxSmall, invalid && styles.dateBoxInvalid)}
        aria-label={`${label} · Datum`}
        placeholder="TT.MM.JJJJ"
        value={text}
        onChange={(event) => setText(event.target.value)}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={commitText}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitText();
            setOpen(false);
          }
        }}
        autoComplete="off"
      />
      {withTime ? (
        <input
          id={`${id}-time`}
          className={clsx(styles.dateBox, styles.timeBox, small && styles.dateBoxSmall)}
          aria-label={`${label} · Uhrzeit`}
          placeholder={timePlaceholder}
          value={time}
          onChange={(event) => onTimeChange(event.target.value)}
          onBlur={(event) => onTimeChange(normalizeTimeInput(event.target.value))}
          autoComplete="off"
          inputMode="numeric"
        />
      ) : null}
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        placement="bottom-start"
        offset={7}
        variant="none"
        initialFocus="none"
        label="Kalender"
        className={styles.calendarPopover}
      >
        <CalendarSheet
          value={date || null}
          onSelect={(key) => {
            onDateChange(key);
            setOpen(false);
          }}
        />
      </Popover>
    </span>
  );
}

/** "Follow-up planen" (III.T3): checkbox, date/time boxes and the title of the task that will be created. */
export function FollowUpField({
  checked,
  onCheckedChange,
  date,
  time,
  onDateChange,
  onTimeChange,
  taskTitle,
  error,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  date: string;
  time: string;
  onDateChange: (key: string) => void;
  onTimeChange: (time: string) => void;
  taskTitle: string;
  error?: string | undefined;
}) {
  return (
    <EditorField label="Folgeschritt" error={error}>
      <div className={styles.followUp}>
        <label className={styles.followUpToggle}>
          <input
            type="checkbox"
            className={styles.listInput}
            checked={checked}
            onChange={(event) => {
              onCheckedChange(event.target.checked);
              if (event.target.checked && !date) onDateChange(addDays(todayKey(), 1));
            }}
          />
          <span className={clsx(styles.followUpBox, checked && styles.followUpBoxOn)} aria-hidden="true">
            {checked ? '✓' : ''}
          </span>
          <span className={styles.followUpLabel}>Follow-up planen</span>
        </label>
        {checked ? (
          <>
            <DateTimeBoxes
              label="Follow-up"
              date={date}
              time={time}
              onDateChange={onDateChange}
              onTimeChange={onTimeChange}
              small
              invalid={Boolean(error)}
            />
            <span className={styles.dateText}>Erzeugt: {taskTitle}</span>
          </>
        ) : null}
      </div>
    </EditorField>
  );
}

/** "Später senden" popover of the split send button (III.T1). */
export function LaterPopover({
  date,
  time,
  onDateChange,
  onTimeChange,
  onCancel,
  onConfirm,
}: {
  date: string;
  time: string;
  onDateChange: (key: string) => void;
  onTimeChange: (time: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [text, setText] = useState(date ? formatDayKey(date) : '');
  useEffect(() => {
    setText(date ? formatDayKey(date) : '');
  }, [date]);
  return (
    <CalendarSheet
      value={date || null}
      onSelect={onDateChange}
      note={null}
      wide
      header={
        <>
          <div className={styles.laterEyebrow}>Später senden</div>
          <div className={styles.laterRow}>
            <input
              className={styles.laterDate}
              aria-label="Versanddatum"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onBlur={() => {
                const parsed = parseGermanDate(text);
                if (parsed) onDateChange(parsed);
                else setText(date ? formatDayKey(date) : '');
              }}
            />
            <input
              className={clsx(styles.laterDate, styles.laterTime)}
              aria-label="Versandzeit"
              value={time}
              placeholder="HH:MM"
              onChange={(event) => onTimeChange(event.target.value)}
              onBlur={(event) => onTimeChange(normalizeTimeInput(event.target.value))}
            />
          </div>
        </>
      }
      footer={
        <div className={styles.laterActions}>
          <button type="button" className={styles.laterCancel} onClick={onCancel}>
            Abbrechen
          </button>
          <button type="button" className={styles.laterPrimary} onClick={onConfirm}>
            Später senden
          </button>
        </div>
      }
    />
  );
}

/** Validation of the time boxes (empty = not set); anything `parseTimeInput` reads („1000", „9.30") is valid (D-441). */
export function validTime(time: string): boolean {
  return time.trim() === '' || parseTimeInput(time) !== null;
}
